<template>
  <div id="app" :style="cssVariables">
    <!-- Network status banner -->
    <div v-if="!isOnline" class="offline-banner">
      You are currently offline
    </div>
    <div v-else-if="connectionQuality === 'slow'" class="slow-connection-banner">
      Slow connection detected
    </div>
    
    <AlertMessage />
    <AdminAlerts />

    <router-view />
  </div>
</template>

<script>
import AlertMessage from './components/notifications/AlertMessage.vue';
import AdminAlerts from './components/notifications/AdminAlerts.vue';
import { useNetworkStatus } from './composables/useNetworkStatus';
import { cssVars } from '@/config/theme';
import { mapActions } from 'vuex';
import { auth } from '@/firebase/init';
import { onAuthStateChanged } from 'firebase/auth';

export default {
  name: 'App',
  components: {
    AlertMessage,
    AdminAlerts,
  },
  setup() {
    const { isOnline, connectionQuality } = useNetworkStatus();
    return { isOnline, connectionQuality };
  },
  data() {
    return {
      cssVariables: cssVars
    };
  },
  async created() {
    // Initialize app with required data if store is available
    if (this.$store) {
      await this.initializeApp();
    } else {
      console.error('Store is not available');
    }
  },
  methods: {
    ...mapActions(['fetchUserProfile', 'setProfile']),
    async initializeApp() {
      try {
        // First fetch user and profile
        console.log('[App] Initializing app - fetching user data');
        if (this.$store && this.$store.dispatch) {
          await this.$store.dispatch('fetchUser');
          
          // Then fetch other data that depends on user being loaded
          console.log('[App] Fetching app dependencies');
          await Promise.all([
            this.$store.dispatch('fetchGlobalSettings'),
          ]);
          
          console.log('App initialized with all required data');
        } else {
          console.error('Store or dispatch method is not available');
        }
      } catch (error) {
        console.error('Error initializing app:', error);
        if (this.$store && this.$store.dispatch) {
          this.$store.dispatch('showAlert', {
            message: "Failed to load application data",
            type: "error"
          });
        }
      }
    },
    async mounted() {
      // Listen for auth state changes
      onAuthStateChanged(auth, async (user) => {
        if (user) {
          console.log('Auth state changed - user signed in:', user.uid);
          
          // Check if we have profile data in a custom claim or need to fetch
          try {
            await this.fetchUserProfile();
            console.log('Profile fetched on auth state change');
          } catch (error) {
            console.error('Failed to fetch profile on auth state change:', error);
          }
        } else {
          console.log('Auth state changed - user signed out');
          // Clear profile when user signs out
          this.setProfile(null);
        }
      });
      
      // Initialize app with required data if store is available
      await this.initializeApp();
    }
  },
  watch: {
    // Watch for user changes to refresh data
    '$store.state.user': {
      handler(newUser, oldUser) {
        if (newUser && (!oldUser || newUser.uid !== oldUser?.uid)) {
          console.log('[App] User changed - refreshing data');
          //this.$store.dispatch('fetchCurrentMonthSalesCount');
        }
      },
      immediate: true
    }
  }
};
</script>


<style>

@import url('https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100..900;1,100..900&display=swap');

* {
  padding: 0;
  margin: 0;
  font-family: "Roboto", sans-serif;
}

body{
  margin: 0;
  padding: 0;
  background-color: #f5f6f8;
}

/*
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}
*/

/* Offline banner styles */
.offline-banner {
  position: fixed;
  top: 0;
  width: 100%;
  background: red;
  color: white;
  text-align: center;
  padding: 10px;
  font-weight: bold;
  z-index: 1000;
}

/* Slow connection banner styles */
.slow-connection-banner {
  position: fixed;
  top: 0;
  width: 100%;
  background: orange;
  color: white;
  text-align: center;
  padding: 10px;
  font-weight: bold;
  z-index: 1000;
}

input, button, select, textarea {
   outline: none;
}

.button-shadow{
     box-shadow: rgba(0, 0, 0, 0.4) 0px 2px 4px, rgba(0, 0, 0, 0.3) 0px 7px 13px -3px, rgba(0, 0, 0, 0.2) 0px -3px 0px inset;
}

</style>
